import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ArrowLeft, Plus, Minus } from "lucide-react";

const BUDGET_CATEGORIES = [
  { key: "comida", label: "Comida", daily: true, maxKey: "max_per_day_comida", default_limit: 800 },
  { key: "transporte", label: "Transporte", daily: false, maxKey: "max_per_day_transporte", default_limit: 1500 },
  { key: "hospedaje", label: "Hospedaje", daily: true, maxKey: "max_per_day_hospedaje", default_limit: 2500 },
  { key: "otros", label: "Otros gastos", daily: false, maxKey: "max_per_day_otros", default_limit: 500 },
];

const FIELD_LABELS = {
  branch_origin_id: "la sucursal de origen",
  destination_description: "el destino",
  trip_start: "la fecha de inicio del viaje",
  trip_end: "la fecha de fin del viaje",
  purpose: "el motivo del viaje",
  budget_breakdown: "el desglose de presupuesto",
};

function parseApiError(e) {
  const detail = e?.response?.data?.detail;
  if (!detail) return "Error al crear la solicitud. Verifica tu conexión e intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => {
      const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : d.loc;
      const label = FIELD_LABELS[field] || field;
      return `Falta indicar ${label}.`;
    });
    return msgs.join(" ");
  }
  return "Error al crear la solicitud.";
}

export default function OpsViaticoNuevo() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [policy, setPolicy] = useState({});
  const [form, setForm] = useState({
    branch_origin_id: "",
    destination_description: "",
    trip_start: "",
    trip_end: "",
    purpose: "",
  });
  const [budget, setBudget] = useState({
    comida: { enabled: false, per_day: 0, total: 0 },
    transporte: { enabled: false, total: 0 },
    hospedaje: { enabled: false, per_day: 0, total: 0 },
    otros: { enabled: false, total: 0 },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.allSettled([api.get("/branches"), api.get("/ops/config")]).then(([brRes, cfgRes]) => {
      const allBranches = brRes.status === "fulfilled" ? (brRes.value?.data || []) : [];
      setBranches(allBranches);
      const policy = cfgRes.status === "fulfilled" ? (cfgRes.value?.data?.viaticos_policy || {}) : {};
      setPolicy(policy);

      // branch_manager: preseleccionar su propia sucursal (primera autorizada)
      if (user?.role === "branch_manager") {
        const authorized = user?.branches_authorized || (user?.branch_id ? [user.branch_id] : []);
        const ownBranch = allBranches.find(b => authorized.includes(b.id));
        if (ownBranch) setForm(f => ({ ...f, branch_origin_id: ownBranch.id }));
      } else if (allBranches.length > 0) {
        setForm(f => ({ ...f, branch_origin_id: allBranches[0].id }));
      }
    });
  }, []);

  const tripDays = () => {
    if (!form.trip_start || !form.trip_end) return 1;
    const d = Math.ceil((new Date(form.trip_end) - new Date(form.trip_start)) / (1000 * 60 * 60 * 24)) + 1;
    return Math.max(1, d);
  };

  const days = tripDays();

  const updateBudget = (cat, field, val) => {
    setBudget(prev => {
      const catData = { ...prev[cat], [field]: val };
      const catMeta = BUDGET_CATEGORIES.find(c => c.key === cat);
      if (catMeta?.daily && field === "per_day") {
        catData.total = val * days;
        catData.days = days;
      }
      if (field === "enabled" && !val) {
        catData.per_day = 0; catData.total = 0;
      }
      return { ...prev, [cat]: catData };
    });
  };

  // Recalculate daily totals when dates change
  useEffect(() => {
    setBudget(prev => {
      const updated = { ...prev };
      BUDGET_CATEGORIES.filter(c => c.daily).forEach(c => {
        if (updated[c.key].enabled && updated[c.key].per_day) {
          updated[c.key] = { ...updated[c.key], total: updated[c.key].per_day * days, days };
        }
      });
      return updated;
    });
  }, [form.trip_start, form.trip_end]);

  const totalRequested = Object.values(budget).filter(b => b.enabled).reduce((s, b) => s + (b.total || 0), 0);

  const buildBudgetBreakdown = () => {
    const bb = {};
    BUDGET_CATEGORIES.forEach(cat => {
      if (budget[cat.key].enabled) {
        const b = budget[cat.key];
        const limit = policy[cat.maxKey] || cat.default_limit;
        bb[cat.key] = cat.daily
          ? { per_day: b.per_day, days, total: b.per_day * days }
          : { total: b.total };
      }
    });
    return bb;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.branch_origin_id || !form.destination_description || !form.trip_start || !form.trip_end || !form.purpose) {
      setError("Completa todos los campos obligatorios."); return;
    }
    if (totalRequested <= 0) {
      setError("Agrega al menos un rubro de gasto al presupuesto."); return;
    }
    setLoading(true); setError("");
    try {
      const res = await api.post("/ops/viaticos", {
        ...form,
        budget_breakdown: buildBudgetBreakdown(),
      });
      navigate(`/ops/viaticos/${res.data.id}`);
    } catch (e) {
      setError(parseApiError(e));
    } finally { setLoading(false); }
  };

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6" data-testid="ops-viatico-nuevo-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/ops/viaticos")} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-xl font-bold text-slate-900">Nueva solicitud de viaticos</h1>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="viatico-form-error">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h3 className="font-semibold text-slate-900">Datos del viaje</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Sucursal de origen *</label>
              {user?.role === "branch_manager" ? (
                <input
                  type="text"
                  value={branches.find(b => b.id === form.branch_origin_id)?.name || ""}
                  disabled
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-slate-100 text-slate-500 cursor-not-allowed"
                  data-testid="viatico-branch-origin-locked"
                />
              ) : (
                <select
                  value={form.branch_origin_id}
                  onChange={set("branch_origin_id")}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  data-testid="viatico-branch-origin-select">
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              )}
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Destino *</label>
              <input type="text" value={form.destination_description} onChange={set("destination_description")}
                placeholder="Ej: Visita a sucursal Xalisco"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" required
                data-testid="viatico-destination-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fecha de salida *</label>
              <input type="date" value={form.trip_start} onChange={set("trip_start")}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" required
                data-testid="viatico-trip-start-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fecha de regreso *</label>
              <input type="date" value={form.trip_end} onChange={set("trip_end")} min={form.trip_start}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" required
                data-testid="viatico-trip-end-input" />
            </div>
            {form.trip_start && form.trip_end && (
              <div className="col-span-2 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                Duracion: {days} dia{days !== 1 ? "s" : ""}
              </div>
            )}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Proposito del viaje *</label>
              <textarea value={form.purpose} onChange={set("purpose")} rows={2}
                placeholder="Ej: Capacitacion gerencial, supervision operativa, reunion con proveedor..."
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none" required
                data-testid="viatico-purpose-input" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h3 className="font-semibold text-slate-900">Presupuesto</h3>
          <p className="text-xs text-slate-500">Activa los rubros que apliquen y completa los montos. Los límites son por día.</p>
          <div className="space-y-3">
            {BUDGET_CATEGORIES.map(cat => {
              const b = budget[cat.key];
              const limit = policy[cat.maxKey] || cat.default_limit;
              const maxAllowed = cat.daily ? limit * days : limit;
              const over = b.enabled && (b.total > maxAllowed);
              return (
                <div key={cat.key} className={`border rounded-xl p-4 transition-colors ${b.enabled ? (over ? "border-red-200 bg-red-50" : "border-yellow-200 bg-yellow-50") : "border-slate-200 bg-slate-50"}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <input type="checkbox" checked={b.enabled} onChange={e => updateBudget(cat.key, "enabled", e.target.checked)}
                      className="w-4 h-4 accent-yellow-400" id={`cat-${cat.key}`} />
                    <label htmlFor={`cat-${cat.key}`} className="font-medium text-slate-900 cursor-pointer">{cat.label}</label>
                    <span className="text-xs text-slate-400 ml-auto">Max: ${maxAllowed.toLocaleString("es-MX")} {cat.daily ? `(${limit}/dia x ${days} dias)` : ""}</span>
                  </div>
                  {b.enabled && (
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      {cat.daily && (
                        <div>
                          <label className="text-xs text-slate-500">Por dia ($)</label>
                          <input type="number" min={0} max={limit} value={b.per_day}
                            onChange={e => updateBudget(cat.key, "per_day", parseFloat(e.target.value) || 0)}
                            className="w-full mt-0.5 border border-slate-200 rounded-lg px-2 py-1.5 text-sm" />
                        </div>
                      )}
                      <div>
                        <label className="text-xs text-slate-500">{cat.daily ? "Total calculado" : "Total ($)"}</label>
                        <input type="number" min={0} value={b.total}
                          readOnly={cat.daily}
                          onChange={e => !cat.daily && updateBudget(cat.key, "total", parseFloat(e.target.value) || 0)}
                          className={`w-full mt-0.5 border border-slate-200 rounded-lg px-2 py-1.5 text-sm ${cat.daily ? "bg-slate-100" : ""}`} />
                      </div>
                    </div>
                  )}
                  {over && (
                    <p className="text-xs text-red-600 mt-1">Excede el limite maximo de ${maxAllowed.toLocaleString("es-MX")}.</p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-900 text-white rounded-xl">
            <span className="font-semibold">Total solicitado</span>
            <span className="text-2xl font-bold">${totalRequested.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={() => navigate("/ops/viaticos")}
            className="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors">
            Cancelar
          </button>
          <button type="submit" disabled={loading}
            className="flex-1 py-2.5 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400 transition-colors disabled:opacity-50"
            data-testid="btn-enviar-solicitud">
            {loading ? "Enviando..." : "Enviar solicitud"}
          </button>
        </div>
      </form>
    </div>
  );
}
